import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubHeader, Button, Alert } from 'vetrf-ui';
import { InfoTable } from 'components/InfoTable';
import { ConfirmModal } from 'components/ConfirmModal';
import { RemoveModal } from 'components/RemoveModal';
import { NestedBlockWithAccordion } from 'components/FormNestedBlock';
import { OrderingWithSignatureModal } from 'components/OrderingWithSignatureModal/OrderingWithSignatureModal';
import { SigningPersonDictOption } from 'api/dict/params';
import { ErulRegistrationCodes } from 'api/erul/types';
import { ElkEpguRequestInfoDTO } from 'api/elkEpgu/types';
import { getTooltipForErul } from 'api/erul/utils';
import { erulApi } from 'api/erul';
import { elkEpguApi } from 'api/elkEpgu';
import { FunctionPermissions, GmpDto, GmpProductType } from 'modules/gmp/gmpRegistry/api/params';
import { gmpFormApi } from '../../../../../api/gmpFormApi';
import {
  erulBlockColumns,
  erulWithStatusBlockColumns,
  mainBlockColumns,
  producerBlockColumns,
  licenseBlockColumns,
  rshnBlockColumns,
  preparationsBlockColumns,
  requestBlockColumns,
  inspectionBlockColumns,
  reportBlockColumns,
  epguRegistrationBlockColumn,
} from './consts';

type InformationProps = {
  data: GmpDto;
  registryLink: string;
  removeMethod: (id: string) => void;
  updateData: () => void;
  permissions: FunctionPermissions;
  cardLink?: string;
  id?: string;
};

export const Information = ({
  data,
  removeMethod,
  updateData,
  registryLink,
  permissions,
  cardLink,
}: InformationProps) => {
  const navigate = useNavigate();
  const [isRemoveCardModalOpen, setIsRemoveCardModalOpen] = useState(false);
  const [isOrderingModalOpen, setIsOrderingModalOpen] = useState(false);
  const [isErulModalOpen, setIsErulModalOpen] = useState(false);
  const [isElkModalOpen, setIsElkModalOpen] = useState(false);
  const [elkRequestInfo, setElkRequestInfo] = useState<ElkEpguRequestInfoDTO>();
  const [error, setError] = useState('');

  const openOrderingModal = () => {
    setIsOrderingModalOpen(true);
  };

  const openRemoveModal = () => {
    setIsRemoveCardModalOpen(true);
  };

  const closeRemoveModal = () => {
    setIsRemoveCardModalOpen(false);
  };

  const closeOrderingModal = () => {
    setIsOrderingModalOpen(false);
  };

  const openErulModal = () => {
    setIsErulModalOpen(true);
  };

  const closeErulModal = () => {
    setIsErulModalOpen(false);
  };

  const openElkEpguModal = () => {
    setIsElkModalOpen(true);
  };

  const closeElkEpguModal = () => {
    setIsElkModalOpen(false);
  };

  const onRemoveCard = async () => {
    await removeMethod(data.id!);
    closeRemoveModal();
    navigate(registryLink);
  };

  const createOrderingWithSignature = (signingPerson: SigningPersonDictOption) => {
    closeOrderingModal();
    setError('');
    const sendData = {
      ...signingPerson,
      id: data.id!,
      signed: false,
    };
    gmpFormApi
      .getPdf(sendData)
      .catch(() => setError('Произошла ошибка при попытке формирования выписки'));
  };

  const createOrdering = () => {
    setError('');
    const sendData = {
      id: data.id!,
      signed: true,
    };
    gmpFormApi
      .getPdf(sendData)
      .catch(() => setError('Произошла ошибка при попытке формирования выписки'));
  };

  const getErulFile = async () => {
    setError('');
    erulApi
      .downloadGmpFileForErul(data.id!)
      .then()
      .catch(async (err) => {
        const errorData = JSON.parse(await err.response.data.text());
        setError(errorData.message);
      });
  };

  const sendToErul = async () => {
    closeErulModal();
    setError('');
    await erulApi
      .sendGmpToErul(data.id!)
      .then(() => updateData())
      .catch((err) => setError(err.response.data.message));
  };

  const registerToEpgu = async () => {
    closeElkEpguModal();
    setError('');
    await elkEpguApi
      .registerGmp(data.id!, elkRequestInfo?.requestType?.id)
      .then(() => updateData())
      .catch((err) => setError(err.response.data.message));
  };

  const mainBlockData = [
    {
      item: {
        registrationNumber: data.registrationNumber,
        registerEntryDate: data.registerEntryDate,
        gmpValidityPeriodDateStart: data.gmpValidityPeriodDateStart,
        gmpValidityPeriodDateEnd: data.gmpValidityPeriodDateEnd,
        gmpConclusionFile: data.gmpConclusionFile,
        dataDate: data.dataDate,
        status: data.status,
      },
    },
  ];

  const erulBlockData = [
    {
      item: {
        erulRegNumber: data?.erulRegNumber,
        erulRegDate: data?.erulRegDate,
        erulManualChanged: data?.erulManualChanged,
        erulRegistration: data?.erulRegistration,
      },
    },
  ];

  const producerBlockData = [
    {
      item: {
        producerRegNumber: data.producer.producerRegNumber,
        producerName: data.producer.name,
        producerAddress:
          data.producer.businessEntity?.juridicalAddress?.addressView ||
          data.producer.mainAddress?.address ||
          '',
        site: data.siteName,
        siteAddress: data.siteAddress,
        id: data.producer.id,
        notLinkedWithCerberus: data.producerNotLinkedWithCerberus,
        incorrectCerberusLink: data.producerIncorrectCerberusLink,
      },
    },
  ];

  const licenseBlockData = [
    {
      item: {
        licenseNumber: data.licenseNumber,
        licenseDate: data.licenseDate,
      },
    },
  ];

  const inspectionBlockData = [
    {
      item: {
        inspectionInitiator: data.pharmaceuticalInspectionInfo.inspectionInitiator,
        inspectionDate: data.pharmaceuticalInspectionInfo.inspectionDate,
        dosageForm: data.pharmaceuticalInspectionInfo.dosageForm,
        notLinkedWithCerberus:
          data.pharmaceuticalInspectionInfo.inspectionInitiatorNotLinkedWithCerberus,
        incorrectCerberusLink:
          data.pharmaceuticalInspectionInfo.inspectionInitiatorIncorrectCerberusLink,
      },
    },
  ];

  const rshnBlockData = [
    {
      item: {
        rshnOrderNumber: data.rshnOrderNumber,
        rshnOrderDate: data.rshnOrderDate,
      },
    },
  ];

  const requestBlockData = [
    {
      item: {
        requestRegNumber: data.requestRegNumber,
        requestRegDate: data.requestRegDate,
        requestMethod: data.requestMethod,
        epguRegistration: data.epguRegistrationList?.length
          ? data.epguRegistrationList[data.epguRegistrationList.length - 1]
          : null,
      },
    },
  ];

  const getPreparationsData = (preparation: GmpProductType) => [
    {
      item: {
        name: preparation.name,
        id: preparation.registryProduct?.entry.id,
        regNumber: preparation.regNumber,
        registryProduct: preparation.registryProduct,
      },
    },
  ];

  useEffect(() => {
    if (permissions.elk) {
      elkEpguApi.getGmpRequestInfo(data.id!).then(setElkRequestInfo);
    }
  }, [permissions.elk]);

  return (
    <>
      {error && (
        <Alert closable messageType="danger">
          {error}
        </Alert>
      )}
      <SubHeader
        header="Информация"
        icon="info"
        iconColor="orange"
        iconSize={120}
        level={4}
        toolbar={
          <div className="info-toolbar">
            {permissions.edit &&
              data.erulRegistration?.status.code !== ErulRegistrationCodes.ERUL_NEW &&
              data.erulRegistration?.status.code !== ErulRegistrationCodes.ERUL_IN_PROGRESS && (
                <Button
                  icon="pencil"
                  iconColor="green"
                  iconSize={150}
                  onClick={() => {
                    navigate(`${cardLink}/edit`);
                  }}
                  onlyIcon
                  tooltip="Редактировать"
                />
              )}
            {permissions.print && (
              <div className="card-btn-group">
                <Button
                  icon=" orderingWithSignature"
                  iconColor="orange"
                  iconSize={150}
                  onClick={createOrdering}
                  onlyIcon
                  tooltip="Формирование выписки с ЭП"
                />
                <Button
                  icon=" ordering"
                  iconColor="orange"
                  iconSize={150}
                  onClick={openOrderingModal}
                  onlyIcon
                  tooltip="Формирование выписки без ЭП"
                />
              </div>
            )}
            {permissions.erul && (
              <div className="card-btn-group">
                <Button
                  icon="file-code-o"
                  iconColor="orange"
                  iconSize={150}
                  onClick={getErulFile}
                  onlyIcon
                  tooltip="Выгрузить данные для регистрации в ЕРУЛ вручную"
                />
                <Button
                  disabled={
                    data.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_NEW ||
                    data.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_IN_PROGRESS
                  }
                  icon="send-o"
                  iconColor="orange"
                  iconSize={150}
                  onClick={openErulModal}
                  onlyIcon
                  tooltip={getTooltipForErul(data.erulRegistration)}
                />
              </div>
            )}
            {permissions.elk && (
              <div className="card-btn-group">
                <Button
                  disabled={!elkRequestInfo?.operationApplicable}
                  icon="id-card"
                  iconColor="blue"
                  iconSize={150}
                  onClick={openElkEpguModal}
                  onlyIcon
                  tooltip="Передача в ЕЛК ЕПГУ статуса заявления о регистрации объекта"
                />
              </div>
            )}
            {permissions.remove && !data.publishedToEaeu && (
              <Button
                icon="trash"
                iconColor="red"
                iconSize={150}
                onClick={openRemoveModal}
                onlyIcon
                tooltip="Удалить"
              />
            )}
          </div>
        }
      />
      <div>
        <NestedBlockWithAccordion title="Сведения заключения о соответствии (GMP)" compact>
          <InfoTable itemList={mainBlockData} rows={mainBlockColumns} />
          <InfoTable
            itemList={erulBlockData}
            rows={permissions.erul ? erulWithStatusBlockColumns : erulBlockColumns}
          />
        </NestedBlockWithAccordion>

        <NestedBlockWithAccordion title="Заявление на получение заключения GMP" compact>
          <InfoTable
            itemList={requestBlockData}
            rows={
              permissions.elk
                ? [...requestBlockColumns, epguRegistrationBlockColumn]
                : requestBlockColumns
            }
          />
        </NestedBlockWithAccordion>

        <NestedBlockWithAccordion title="Производитель" compact>
          <InfoTable itemList={producerBlockData} rows={producerBlockColumns} />
        </NestedBlockWithAccordion>

        <NestedBlockWithAccordion title="Реквизиты лицензии" compact>
          <InfoTable itemList={licenseBlockData} rows={licenseBlockColumns} />
        </NestedBlockWithAccordion>

        <NestedBlockWithAccordion title="Сведения о проведенной фармацевтической инспекции" compact>
          <InfoTable itemList={inspectionBlockData} rows={inspectionBlockColumns} />
          {data.pharmaceuticalInspectionInfo?.results?.map((report) => (
            <Fragment key={report.id}>
              <p className="mb-5">Отчет о проведенной фармацевтической инспекции</p>
              <InfoTable itemList={[{ item: { ...report } }]} rows={reportBlockColumns} />
            </Fragment>
          ))}
        </NestedBlockWithAccordion>

        <NestedBlockWithAccordion title="Приказ РСХН" compact>
          <InfoTable itemList={rshnBlockData} rows={rshnBlockColumns} />
        </NestedBlockWithAccordion>

        <NestedBlockWithAccordion title="Лекарственные средства" compact>
          {data.pharmaceuticalList?.map((el) => (
            <InfoTable
              key={el.regNumber}
              itemList={getPreparationsData(el)}
              rows={preparationsBlockColumns}
            />
          ))}
        </NestedBlockWithAccordion>

        {isOrderingModalOpen && (
          <OrderingWithSignatureModal
            onClose={closeOrderingModal}
            onSubmit={createOrderingWithSignature}
          />
        )}
        {isRemoveCardModalOpen && (
          <RemoveModal
            onClose={closeRemoveModal}
            onRemove={onRemoveCard}
            modalContent="Вы уверены, что хотите удалить запись?"
          />
        )}
        {isErulModalOpen && (
          <ConfirmModal
            onClose={closeErulModal}
            onConfirm={sendToErul}
            modalContent="Вы уверены, что хотите отправить запрос на регистрацию в ЕРУЛ?"
          />
        )}
        {isElkModalOpen && (
          <ConfirmModal
            onClose={closeElkEpguModal}
            onConfirm={registerToEpgu}
            modalContent={`Вы уверены, что хотите выполнить по заявлению ${elkRequestInfo?.requestNumber} от ${elkRequestInfo?.requestDate} запрос "${elkRequestInfo?.requestType?.name}"?`}
          />
        )}
      </div>
    </>
  );
};
